import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchEvalMarkingRuns, fetchProductionJudgementDates } from '../services/api';
import {
  AiJudgementSource,
  AiJudgementSourceKind,
  EvalMarkingRun,
  PRODUCTION_JUDGEMENT_SOURCE,
} from '../types';

const STORAGE_KEY = 'labelling.aiJudgementSource';
const PARAM_SOURCE = 'ai_source';
const PARAM_RUN = 'marking_run_id';

const DEFAULT_EVAL_SOURCE: AiJudgementSource = { kind: 'eval', markingRunId: null };

type PreferenceOrigin = 'url' | 'storage' | 'default' | 'explicit';

const readStoredSource = (): AiJudgementSource | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AiJudgementSource>;
    if (parsed.kind === 'eval') {
      return DEFAULT_EVAL_SOURCE;
    }
    if (parsed.kind === 'production') {
      return PRODUCTION_JUDGEMENT_SOURCE;
    }
  } catch {
    // Corrupt or unavailable storage uses the Eval Run default.
  }
  return null;
};

const persistSource = (source: AiJudgementSource): void => {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(source));
  const url = new URL(window.location.href);
  url.searchParams.delete(PARAM_RUN);
  if (source.kind === 'production') {
    url.searchParams.delete(PARAM_SOURCE);
  } else {
    url.searchParams.set(PARAM_SOURCE, 'eval');
  }
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
};

const readUrlSource = (): AiJudgementSource | null => {
  const params = new URLSearchParams(window.location.search);
  const kind = params.get(PARAM_SOURCE);
  if (kind === 'eval') {
    return DEFAULT_EVAL_SOURCE;
  }
  if (kind === 'production') {
    return PRODUCTION_JUDGEMENT_SOURCE;
  }
  return null;
};

const readInitialPreference = (): { source: AiJudgementSource; origin: PreferenceOrigin } => {
  const url = readUrlSource();
  if (url) return { source: url, origin: 'url' };
  const stored = readStoredSource();
  if (stored) return { source: stored, origin: 'storage' };
  return { source: DEFAULT_EVAL_SOURCE, origin: 'default' };
};

export const applyEvalRunsToSource = (
  prev: AiJudgementSource,
  _origin: PreferenceOrigin,
  _runs: EvalMarkingRun[],
  _loadFailed: boolean
): { source: AiJudgementSource; fellBack: boolean } => {
  if (prev.kind === 'production') {
    return { source: prev, fellBack: false };
  }
  return { source: DEFAULT_EVAL_SOURCE, fellBack: false };
};

export const formatJudgementDateRange = (
  fromIso: string | null | undefined,
  toIso?: string | null
): string | null => {
  const parse = (raw: string | null | undefined): Date | null => {
    if (!raw) return null;
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
  };
  const from = parse(fromIso);
  if (!from) return null;
  const to = parse(toIso) ?? from;
  const fmt = (date: Date) =>
    date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  const sameDay =
    from.getUTCFullYear() === to.getUTCFullYear() &&
    from.getUTCMonth() === to.getUTCMonth() &&
    from.getUTCDate() === to.getUTCDate();
  return sameDay ? fmt(from) : `${fmt(from)} – ${fmt(to)}`;
};

export const evalRunForCriterion = (
  runs: EvalMarkingRun[],
  criteriaId: string | null
): EvalMarkingRun | null => {
  if (!criteriaId) return null;
  return runs.find(run => run.labellingCriterionIds.includes(criteriaId)) ?? null;
};

export interface UseJudgementSourceResult {
  source: AiJudgementSource;
  setKind: (kind: AiJudgementSourceKind) => void;
  evalRuns: EvalMarkingRun[];
  productionFrom: string | null;
  productionTo: string | null;
  loadingEvalRuns: boolean;
  evalRunsError: boolean;
  usedProductionFallback: boolean;
  isReady: boolean;
}

export const useJudgementSource = (): UseJudgementSourceResult => {
  const initial = useMemo(() => readInitialPreference(), []);
  const originRef = useRef<PreferenceOrigin>(initial.origin);
  const [source, setSource] = useState<AiJudgementSource>(initial.source);
  const sourceRef = useRef(source);
  sourceRef.current = source;
  const [evalRuns, setEvalRuns] = useState<EvalMarkingRun[]>([]);
  const [productionFrom, setProductionFrom] = useState<string | null>(null);
  const [productionTo, setProductionTo] = useState<string | null>(null);
  const [loadingEvalRuns, setLoadingEvalRuns] = useState(true);
  const [evalRunsError, setEvalRunsError] = useState(false);
  const [usedProductionFallback, setUsedProductionFallback] = useState(false);
  const [loadingProductionDates, setLoadingProductionDates] = useState(false);

  useEffect(() => {
    if (source.kind === 'production' && productionFrom === null && !loadingProductionDates) {
      setLoadingProductionDates(true);
      fetchProductionJudgementDates()
        .then(dates => {
          setProductionFrom(dates.from);
          setProductionTo(dates.to);
          setLoadingProductionDates(false);
        })
        .catch(() => {
          setLoadingProductionDates(false);
        });
    }
  }, [source.kind, productionFrom, loadingProductionDates]);

  useEffect(() => {
    // Persist production vs Eval so Disagreement Review, User vs Ground Truth,
    // and Output Review keep the same source. Eval Run is resolved per Criterion.
    persistSource(source);
  }, [source]);

  useEffect(() => {
    fetchEvalMarkingRuns()
      .then(catalog => {
        setEvalRuns(catalog.runs);
        setProductionFrom(catalog.productionFrom);
        setProductionTo(catalog.productionTo);
        setEvalRunsError(false);
        setLoadingEvalRuns(false);
        const resolved = applyEvalRunsToSource(
          sourceRef.current,
          originRef.current,
          catalog.runs,
          false
        );
        setSource(resolved.source);
        setUsedProductionFallback(resolved.fellBack);
      })
      .catch(() => {
        setEvalRuns([]);
        setProductionFrom(null);
        setProductionTo(null);
        setEvalRunsError(true);
        setLoadingEvalRuns(false);
        const resolved = applyEvalRunsToSource(sourceRef.current, originRef.current, [], true);
        setSource(resolved.source);
        setUsedProductionFallback(resolved.fellBack);
      });
  }, []);

  const setKind = useCallback((kind: AiJudgementSourceKind) => {
    originRef.current = 'explicit';
    setUsedProductionFallback(false);
    setSource(kind === 'production' ? PRODUCTION_JUDGEMENT_SOURCE : DEFAULT_EVAL_SOURCE);
  }, []);

  return {
    source,
    setKind,
    evalRuns,
    productionFrom,
    productionTo,
    loadingEvalRuns,
    evalRunsError,
    usedProductionFallback,
    isReady: !loadingEvalRuns,
  };
};
